import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from "@angular/core";

import { RoundPipe } from "@pipes";
import { AnalyticsService, EventCategory } from "@services";
import { Calculator, Utils } from "@utils";

import { AbstractStoppableComponent } from "../abstract/abstract-stoppable.component";
import { ButtonComponent } from "../button/button.component";
import { SvgComponent } from "../svg/svg.component";

@Component({
  selector: "mn-amount",
  styleUrls: ["./amount.component.scss"],
  templateUrl: "./amount.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, RoundPipe, ButtonComponent],
  host: { "[class.disabled]": "disabled()" },
})
export class AmountComponent extends AbstractStoppableComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly anS = inject(AnalyticsService);

  private readonly pEl = viewChild.required("pEl", { read: ButtonComponent });
  private readonly mEl = viewChild.required("mEl", { read: ButtonComponent });

  public amount = input<string>("");
  public round = input<boolean>(true);
  public name = input<string>("");
  public min = input<string>("");
  public prefix = input<string>("");
  public precision = input<number>(0);
  public disabled = input<boolean>(false);

  public amountChange = output<string>();
  public amountClicked = output<Event>();

  public aW = signal<number>(0);
  public aC = signal<number>(1);

  private presets: string[] = [];
  private interval: ReturnType<typeof setInterval> | null = null;
  private timeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();

    effect(() => {
      this.aC.set(this.amount().length + this.prefix().length + 1);
    });
  }

  private readonly reflowFn = (): void => {
    this.aW.set(this.eR.nativeElement.offsetWidth);
  };
  private readonly minus = (): void => {
    this.sC(false);
  };
  private readonly plus = (): void => {
    this.sC(true);
  };

  private readonly stop = (): void => {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  };

  public ngOnInit(): void {
    const exp = Calculator.exp(this.min());

    this.presets = [...Array(8).keys()].flatMap(i => {
      const factor = Calculator.pow(exp + 1 + i);
      const factor5 = Calculator.mult("5", factor);
      return [factor, factor5];
    });
  }

  public ngAfterViewInit(): void {
    this.reflowFn();

    window.addEventListener("resize", this.reflowFn);
    this.pEl().eR.nativeElement.addEventListener("pointerdown", this.plus);
    this.mEl().eR.nativeElement.addEventListener("pointerdown", this.minus);
    document.addEventListener("pointerup", this.stop, true);
    document.addEventListener("pointercancel", this.stop, true);
  }

  public ngOnDestroy(): void {
    this.stop();

    window.removeEventListener("resize", this.reflowFn);
    this.pEl().eR.nativeElement.removeEventListener("pointerdown", this.plus);
    this.mEl().eR.nativeElement.removeEventListener("pointerdown", this.minus);
    document.removeEventListener("pointerup", this.stop, true);
    document.removeEventListener("pointercancel", this.stop, true);
  }

  private sC(plus: boolean): void {
    this.stop();

    const change = (): void => {
      const mult = this.gM(plus);
      const amount = this.amount();

      this.eC(plus ? Calculator.sum(amount, mult) : Calculator.min(amount, mult));
    };

    change();

    const event = `amount_${plus ? "plus" : "minus"}_operator`;
    this.anS.client(event);
    this.interval = setInterval(change, Utils.duration);
    this.timeout = setTimeout(() => this.anS.track(`${event}_held`, EventCategory.C), Utils.duration);
  }

  private eC(value: string): void {
    let amount = value;
    if (Calculator.lte(value, "0")) {
      amount = "0";
      this.stop();
    }
    this.amountChange.emit(amount);
  }

  private gM(plus: boolean): string {
    const amount = this.amount();

    return Calculator.mult(
      this.presets.find(preset => {
        return plus ? Calculator.lt(amount, preset) : Calculator.lte(amount, preset);
      }) ?? this.presets.at(-1)!,
      "0.1",
    );
  }
}
